import { ERROR_CODES, type ApiErrorBody, type ApiErrorTest } from '@lims/shared-types';

/**
 * The typed API client.
 *
 * The frontend reaches the backend ONLY through this module. It holds no
 * database credentials and issues no queries — every rule, every permission
 * check and every tenant decision happens on the other side of this boundary.
 *
 * The API returns stable error CODES, never user-facing prose. This client
 * surfaces the code, and the interface renders the translated message from the
 * i18n catalogue, which is what keeps the API locale-independent and reusable
 * by a future mobile client.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    message: string,
    /**
     * Per-field problems. `message` carries a machine-readable value the client
     * needs — the matched client id on a duplicate warning, for instance. It is
     * NEVER rendered as prose: the API returns codes and the interface renders
     * its own translated text.
     */
    readonly details: readonly {
      field?: string;
      code?: string;
      message?: string;
      /** The tests a refusal names, from the server's catalogue (D325). */
      tests?: readonly ApiErrorTest[];
    }[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the caller should be sent back to the sign-in page. */
  get isAuthFailure(): boolean {
    return (
      this.code === ERROR_CODES.NOT_AUTHENTICATED ||
      this.code === ERROR_CODES.TOKEN_EXPIRED ||
      this.code === ERROR_CODES.ACCOUNT_DEACTIVATED
    );
  }

  /** Field name to error code, for highlighting inputs on a form. */
  get fieldErrors(): Record<string, string> {
    const output: Record<string, string> = {};
    for (const detail of this.details) {
      if (detail.field) output[detail.field] = detail.code ?? 'invalid';
    }
    return output;
  }
}

// ---------------------------------------------------------------------------
// Session storage
// ---------------------------------------------------------------------------

const ACCESS_TOKEN_KEY = 'lims.accessToken';
const REFRESH_TOKEN_KEY = 'lims.refreshToken';

/**
 * Tokens live in `localStorage`.
 *
 * A deliberate, recorded trade-off: it keeps the API stateless and identical
 * for a future mobile client, which is a stated architectural requirement.
 * The exposure it accepts is script access, and the mitigations are elsewhere
 * — short access-token lifetime, refresh rotation with reuse detection, and
 * immediate server-side revocation on sign-out or deactivation. Reading a
 * token from storage never grants anything the server has already revoked.
 */
export const session = {
  accessToken(): string | null {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  refreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  store(accessToken: string, refreshToken: string): void {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } catch {
      // A browser with storage blocked still works for the current page load.
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // Nothing to do.
    }
  },
};

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  /**
   * A multipart body. Sent as-is, WITHOUT a `Content-Type` header: the browser
   * must set it itself so it can append the multipart boundary. Setting it by
   * hand is the classic way to make every upload fail.
   */
  readonly formData?: FormData;
  readonly query?: Record<string, string | number | boolean | undefined>;
  /** Skip the bearer token, for the sign-in and password-reset endpoints. */
  readonly anonymous?: boolean;
  /** Internal: prevents a refresh loop. */
  readonly retrying?: boolean;
}

function buildUrl(
  path: string,
  query?: RequestOptions['query'],
): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

/** Called when a session cannot be recovered, so the app can sign the user out. */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchange the refresh token for a new session.
 *
 * Deduplicated: several requests failing at once must not each rotate the
 * token, because the second rotation would look like a replay and revoke the
 * whole chain.
 */
async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = session.refreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const body = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
      };
      session.store(body.accessToken, body.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * The base path the API is served under, for building a URL a BROWSER will
 * fetch on its own — an `<img src>`, or a link opened in a new tab.
 *
 * Exported because a signed download link comes back from the API as a path
 * relative to that base (`/files/download?token=...`), never as an absolute
 * URL: the API does not tell a client what host it is behind.
 */
export function apiUrl(path: string): string {
  return `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Resolve a path the API returned against the API base.
 *
 * The API returns paths already carrying the `/api/v1` prefix (a signed
 * download link, a branding logo), so those are used as they are. Anything
 * else is treated as relative to the base.
 */
export function resolveApiPath(path: string): string {
  return path.startsWith('/api/') ? path : apiUrl(path);
}

/**
 * How long the interface waits for an answer before it stops waiting.
 *
 * Without a limit a request the network swallowed left a save button spinning
 * for ever, with the user unable to tell a slow save from a lost one. An
 * upload gets longer: a calibration certificate on a poor connection is slow,
 * not lost.
 */
export const REQUEST_TIMEOUT_MS = 30_000;
export const UPLOAD_TIMEOUT_MS = 120_000;

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    options.formData !== undefined ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS,
  );
  try {
    return await sendRequest<T>(path, options, controller.signal);
  } catch (error) {
    if (controller.signal.aborted && !(error instanceof ApiError && error.status > 0)) {
      // HONEST ABOUT WHAT IS NOT KNOWN: the server may have finished the work
      // after the interface stopped waiting. The message says so, rather than
      // "failed", so nobody saves a second copy without looking first.
      throw new ApiError(
        'REQUEST_TIMEOUT',
        0,
        'The server did not answer in time.',
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

async function sendRequest<T>(
  path: string,
  options: RequestOptions,
  signal: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = {};

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (!options.anonymous) {
    const token = session.accessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      signal,
      ...(options.formData !== undefined
        ? { body: options.formData }
        : options.body !== undefined
          ? { body: JSON.stringify(options.body) }
          : {}),
    });
  } catch {
    throw new ApiError(
      'NETWORK_ERROR',
      0,
      'The server could not be reached.',
    );
  }

  // A 401 on an authenticated call is worth one refresh attempt. If that
  // fails, the session is genuinely gone and the user is signed out.
  if (response.status === 401 && !options.anonymous && !options.retrying) {
    if (await refreshSession()) {
      return sendRequest<T>(path, { ...options, retrying: true }, signal);
    }
    session.clear();
    onSessionLost?.();
  }

  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  // Stopped waiting half-way through the body: nothing read may be trusted.
  if (signal.aborted) {
    throw new ApiError('NETWORK_ERROR', 0, 'The server could not be reached.');
  }

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiError(
      body?.error?.code ?? ERROR_CODES.INTERNAL_ERROR,
      response.status,
      body?.error?.message ?? 'The request failed.',
      body?.error?.details ?? [],
      body?.error?.requestId,
    );
  }

  return payload as T;
}

/**
 * Fetch a file through an authenticated route and return its bytes.
 *
 * For files that belong to a record with its own permission — a sample photo,
 * an attempt's worksheet. The server serves those ONLY through the owning
 * record's route (decision D92) and will not mint a signed link for them, so
 * the bearer token travels in a header exactly as it does for any other call.
 */
export async function apiDownload(path: string, retrying = false): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = session.accessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path), { headers });
  } catch {
    throw new ApiError('NETWORK_ERROR', 0, 'The server could not be reached.');
  }

  if (response.status === 401 && !retrying) {
    if (await refreshSession()) return apiDownload(path, true);
    session.clear();
    onSessionLost?.();
  }

  if (!response.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new ApiError(
      body?.error?.code ?? ERROR_CODES.INTERNAL_ERROR,
      response.status,
      body?.error?.message ?? 'The request failed.',
      body?.error?.details ?? [],
      body?.error?.requestId,
    );
  }

  return response.blob();
}
